import { CircularProgress, IconButton, DialogActions } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { makeStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import { useState, useContext, ReactNode, useEffect } from 'react';
import { Field, Form } from 'react-final-form';
import { Button, FormGroup, Modal, ModalV2, Text } from '.';
import { FounderId } from '../../api/founders';
import mentorsAPI, { MentorId } from '../../api/mentors';
import venturesAPI from '../../api/ventures';
import { VentureId } from '../../api/ventures/types/Venture';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { UserContext } from '../../contexts/user-context';
import { lengthField } from '../../utils/form';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import { TestId } from '../Testing/TestId';
import {
  FormRadioboxGroup,
  FormSelect,
  TextFieldWysiwyg,
} from '../forms/wrappers';
import { OptionProps } from './select';

type ManagementRequestType = 'ADD' | 'REMOVE' | 'CHANGE';

interface FormValues {
  description: string;
  type: ManagementRequestType;
  ventureId: VentureId | null;
  mentorId: MentorId | null;
}

type Errors = {
  [K in keyof FormValues]?: string;
};

type ValidateFormOptions = {
  isMentorRequired?: boolean;
};

const validateForm = (options?: ValidateFormOptions) => {
  return (values: FormValues) => {
    const errors: Errors = {};
    if (!values.description) {
      errors.description = 'Required';
    }
    if (!values.ventureId) {
      errors.ventureId = 'Required';
    }
    if (!values.mentorId && options?.isMentorRequired) {
      errors.mentorId = 'Required';
    }
    return errors;
  };
};

interface MentorManagementRequestProps {
  ventureId: VentureId | null;
  mentorId: MentorId | null;
  defaultType: ManagementRequestType;
  selectableType?: boolean;
  ventureOptions?: OptionProps<VentureId>[];
  mentorOptions?: OptionProps<MentorId>[];
}

interface MentorManagementRequestIconButtonProps
  extends MentorManagementRequestProps {
  title: string;
  caption?: string;
}

interface MentorManagementRequestButtonProps
  extends MentorManagementRequestProps {
  buttonText: string;
  title: string;
  caption?: string;
  startIcon?: ReactNode;
  disabledButton?: boolean;
  testid?: string;
  founderId?: string; // Add founderId for venture loading
}

interface MentorManagementRequestFormProps
  extends MentorManagementRequestProps {
  handleClose: Function;
}

interface MentorManagementRequestModalProps
  extends MentorManagementRequestProps {
  title: string;
  caption?: string;
  open: boolean;
  onClose: () => void;
  founderId?: string; // Add founderId for venture loading
}

const useTitleStyles = makeStyles({
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  contentButton: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    columnGap: 15,
    marginTop: 20,
  },
  warningText: {
    width: '100%',
    marginBottom: '16px',
    display: 'block',
  },
  warningActions: {
    padding: '0 !important',
    margin: '0 !important',
    justifyContent: 'flex-end',
  },
});

export function MentorManagementRequestModal({
  title,
  caption,
  ventureId,
  mentorId,
  founderId,
  selectableType,
  defaultType,
  ventureOptions,
  mentorOptions,
  open,
  onClose,
}: MentorManagementRequestModalProps) {
  const classes = useTitleStyles();
  const { rb } = useResourceBundles();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoadingVentures, setIsLoadingVentures] = useState(false);
  const [ventures, setVentures] = useState<OptionProps<VentureId>[]>([]);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const hasVentureId = Boolean(ventureId);
  const hasVentureOptions = Boolean(ventureOptions?.length);
  const shouldLoadVentures =
    open && !hasVentureId && !hasVentureOptions && founderId && mentorId;

  useEffect(() => {
    if (shouldLoadVentures) {
      const loadVentures = async () => {
        try {
          setIsLoadingVentures(true);
          const loadedVentures =
            await venturesAPI.getVenturesByFounderAndMentor(
              founderId!,
              mentorId!,
            );

          if (loadedVentures.length === 0) {
            setVentures([]);
            setShowWarningModal(true);
          } else {
            const ventureOptions = loadedVentures.map((venture) => ({
              value: venture.id as VentureId,
              label: venture.ventureName,
            }));
            setVentures(ventureOptions);
          }
        } catch (error: any) {
          console.error('Error loading ventures:', error);
          enqueueSnackbar('Failed to load ventures', { variant: 'error' });
        } finally {
          setIsLoadingVentures(false);
        }
      };

      loadVentures();
    } else if (open && (hasVentureId || hasVentureOptions)) {
      setIsLoadingVentures(false);
    }
  }, [
    shouldLoadVentures,
    founderId,
    mentorId,
    enqueueSnackbar,
    open,
    hasVentureId,
    hasVentureOptions,
  ]);

  const handleClose = () => {
    setTimeout(() => {
      setVentures([]);
      setShowWarningModal(false);
    }, 300);
    onClose();
  };

  const handleWarningClose = () => {
    setTimeout(() => {
      setShowWarningModal(false);
    }, 300);
    onClose();
  };

  const renderContent = () => {
    if (showWarningModal) {
      return (
        <>
          <Text variant='normal' className={classes.warningText}>
            You don't have any common ventures with this {rb('mentor')}.
            Management requests can only be made for {rb('mentor')}s you work
            with.
          </Text>
          <DialogActions className={classes.warningActions}>
            <Button
              variant='outlined'
              onClick={handleWarningClose}
              data-testid='button-ok'>
              Ok
            </Button>
          </DialogActions>
        </>
      );
    }

    if (isLoadingVentures) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '40px',
          }}>
          <CircularProgress size={40} />
        </div>
      );
    }

    return (
      <MentorManagementRequestForm
        handleClose={handleClose}
        ventureId={ventureId}
        mentorId={mentorId}
        selectableType={selectableType}
        defaultType={defaultType}
        ventureOptions={hasVentureOptions ? ventureOptions : ventures}
        mentorOptions={mentorOptions}
      />
    );
  };

  return (
    <ModalV2
      width={showWarningModal || isLoadingVentures ? '480px' : '720px'}
      open={open}
      onClose={handleClose}
      titleProps={{
        title: showWarningModal ? 'Cannot manage mentor' : title,
        caption: showWarningModal ? undefined : caption,
      }}>
      {renderContent()}
    </ModalV2>
  );
}

function MentorManagementRequestButton({
  buttonText,
  title,
  caption,
  ventureId,
  mentorId,
  founderId,
  startIcon,
  selectableType,
  defaultType,
  ventureOptions,
  mentorOptions,
  disabledButton,
  testid,
}: MentorManagementRequestButtonProps) {
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  return (
    <>
      <MentorManagementRequestModal
        title={title}
        caption={caption}
        ventureId={ventureId}
        mentorId={mentorId}
        founderId={founderId}
        selectableType={selectableType}
        defaultType={defaultType}
        ventureOptions={ventureOptions}
        mentorOptions={mentorOptions}
        open={open}
        onClose={handleClose}
      />
      <Button
        onClick={handleOpen}
        className={CLASS_TRACKING.ENTITY_ACTION}
        data-testid={testid || 'mentor-management-request-button'}
        variant='contained'
        startIcon={startIcon}
        disabled={disabledButton}
        color='primary'>
        {buttonText}
      </Button>
    </>
  );
}

export function MentorManagementRequestIconButton({
  title,
  caption,
  ventureId,
  mentorId,
  selectableType,
  defaultType,
  ventureOptions,
}: MentorManagementRequestIconButtonProps) {
  return (
    <Modal
      title={title}
      caption={caption}
      contentRenderer={({ handleClose }) => (
        <MentorManagementRequestForm
          handleClose={handleClose}
          ventureId={ventureId}
          mentorId={mentorId}
          selectableType={selectableType}
          defaultType={defaultType}
          ventureOptions={ventureOptions}
        />
      )}
      buttonRenderer={({ onClick }) => (
        <IconButton
          onClick={onClick}
          data-testid={'mentor-management-request-icon'}>
          <EditIcon />
        </IconButton>
      )}
      width={720}
    />
  );
}

function MentorManagementRequestForm({
  handleClose,
  ventureId,
  mentorId,
  defaultType,
  selectableType,
  ventureOptions,
  mentorOptions,
}: MentorManagementRequestFormProps) {
  const initialValues: FormValues = {
    description: '',
    type: defaultType,
    mentorId,
    ventureId,
  };
  const classes = useTitleStyles();
  const { rb } = useResourceBundles();

  const [isLoading, setLoading] = useState(false);
  const { user, identityid } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (formValues: FormValues) => {
    try {
      setLoading(true);
      await mentorsAPI.createMentorManagementRequest({
        description: formValues.description,
        tenantId: user?.id || null,
        ventureId: formValues.ventureId,
        founderId: identityid as FounderId,
        mentorId: formValues.mentorId,
        type: formValues.type,
      });
      setLoading(false);
      handleClose();
      enqueueSnackbar(`${rb('mentor-u')} request sent successfully`, {
        variant: 'success',
      });
    } catch (error: any) {
      setLoading(false);
      const messageError = `Unfortunately, an error occurred when sending the ${rb(
        'mentor',
      )} request.\n Details: ${error?.response?.data?.message || 'Internal server error'
        }`;
      enqueueSnackbar(messageError, {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  return (
    <div>
      <Form
        validate={validateForm({ isMentorRequired: !!mentorOptions })}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(formProps) => (
          <form noValidate>
            {selectableType && (
              <FormGroup>
                <Field<string>
                  name='type'
                  component={FormRadioboxGroup}
                  label={`What would you like to change about this ${rb(
                    'mentor',
                  )}?`}
                  info={`It is totally fine to make adjustments to your ${rb(
                    'mentor',
                  )} team when necessary. Please provide some details and we'll send an email to your program administrator on your behalf.`}
                  options={[
                    {
                      value: 'REMOVE',
                      label: `Request that this ${rb('mentor')} be removed`,
                    },
                    {
                      value: 'CHANGE',
                      label: `Request that this ${rb('mentor')} be replaced`,
                    },
                  ]}
                  testid='mentors-management-request-type'
                />
              </FormGroup>
            )}
            {mentorOptions && (
              <FormGroup>
                <Field<string>
                  name='mentorId'
                  component={FormSelect}
                  label={`${rb('mentor-u')}*`}
                  options={mentorOptions}
                  testid='mentors-management-request-mentor'
                />
              </FormGroup>
            )}
            {ventureOptions && ventureOptions.length > 0 && (
              <FormGroup>
                <Field<string>
                  name='ventureId'
                  component={FormSelect}
                  label='Venture*'
                  options={ventureOptions}
                  testid='mentors-management-request-venture'
                />
              </FormGroup>
            )}
            <FormGroup>
              <Field<string>
                name='description'
                component={TextFieldWysiwyg}
                label='Description*'
                placeholder='Type here...'
                testid='mentors-management-request-description'
                showCount
                maxLength={lengthField.wysiwygDisplayLimit}
              />
            </FormGroup>
            <div className={classes.contentButton}>
              <Button onClick={() => handleClose()} variant='outlined'>
                Cancel
              </Button>
              <TestId testId='mentors-management-request-submit-button'>
                <Button onClick={formProps.handleSubmit} disabled={isLoading}>
                  {isLoading ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Submit'
                  )}
                </Button>
              </TestId>
            </div>
          </form>
        )}
      />
    </div>
  );
}

export default MentorManagementRequestButton;
